import {
  Button,
  Checkbox,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import AlenProductConfig from "src/services/alen/alenDeviceConfig";
import { getAlenDeviceShadow } from "src/services/alen/alenDeviceConfig/utils";
import ToastMaker from "src/components/UI/NotificationToast";
import AlenIndividualActionModal from "./AlenIndividualActionModal";
import SelectedDeviceActionCard from "./SelectedDeviceActionCard";
import DeviceTypeFilter, { ALL_DEVICE_TYPE } from "./DeviceTypeFilter";
import AlenDeviceCard from "./AlenDeviceCard";
import { FirmwareUpdateContextProvider } from "./FirmwareUpdate/FirmwareUpdateContext";
import CheckFirmwareUpdateModal from "./CheckFirmwareUpdateModal";
import FirmwareUpdateProgressModal from "./FirmwareUpdateProgressModal";

const Toast = ToastMaker();

export type AlenDeviceListProps = {
  isLoading: boolean;
  devices: AlenDevice[];
  refetchDevices: () => void;
};

export default function AlenDeviceList({
  isLoading,
  devices,
  refetchDevices,
}: AlenDeviceListProps) {
  const [individualActionModalOpened, setIndividualActionModalOpened] =
    useState(false);

  const [selectedDevice, setSelectedDevice] = useState<
    AlenDevice | undefined
  >();
  const [selectedStandardName, setSelectedStandardName] = useState<
    string | undefined
  >();
  const [currentSelectedValue, setCurrentSelectedValue] = useState<string>("");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const handleStatusClick = ({
    device,
    standardName,
    currentValue,
  }: {
    device: AlenDevice;
    standardName: string;
    currentValue: string;
  }) => {
    setSelectedDevice(device);
    setSelectedStandardName(standardName);
    setCurrentSelectedValue(currentValue);
    setIndividualActionModalOpened(true);
  };

  const selectDevice = (selection: string) => () => {
    setSelectedDeviceIds((prev) => [...prev, selection]);
  };
  const deselectDevice = (deviceId: string) => () => {
    setSelectedDeviceIds(
      selectedDeviceIds.filter((selection) => selection !== deviceId)
    );
  };

  const selectedDevices = devices.filter((device) =>
    selectedDeviceIds.includes(device.id)
  );

  const [visibleDevices, setVisibleDevices] = useState(devices);
  const [devicesTypes, setDevicesTypes] = useState<string[]>([]);
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<string>(ALL_DEVICE_TYPE);

  useEffect(() => {
    const types = devices.map((device) => device.product_name);
    const uniqueTypes = Array.from(new Set(types));
    setDevicesTypes(uniqueTypes);
  }, [devices]);

  useEffect(() => {
    const AQMDevices = devices
      .filter((device) => device.product_name === "AQM")
      .sort((a, b) => a.name.localeCompare(b.name));
    const otherDevices = devices
      .filter((device) => device.product_name !== "AQM")
      .sort((a, b) => a.name.localeCompare(b.name));
    const sortedDevices = [...otherDevices, ...AQMDevices];
    setVisibleDevices(
      selectedDeviceType === ALL_DEVICE_TYPE
        ? sortedDevices
        : sortedDevices.filter(
            (device) => selectedDeviceType === device.product_name
          )
    );
  }, [devices, selectedDeviceType]);
  const showSuccessToast = () => {
    Toast.emit({
      delay: 5000,
      type: "Success",
      title: "Action Completed",
      content: `Successfully Performed Action`,
    });
  };

  const allDeviceSelected =
    selectedDeviceIds.length ===
    devices.filter((d) => d.product_name !== "AQM").length;

  return isLoading ? (
    <LinearProgress />
  ) : (
    <FirmwareUpdateContextProvider>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          marginTop: 3,
          marginBottom: 4,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography>
            Select device(s) to change current status and add new schedules
          </Typography>
          <Button
            color="primary"
            onClick={() =>
              allDeviceSelected
                ? setSelectedDeviceIds([])
                : setSelectedDeviceIds(
                    devices
                      .filter((device) => device.product_name !== "AQM")
                      .map((device) => device.id)
                  )
            }
          >
            {allDeviceSelected ? "Deselect All" : "Select All"}
          </Button>
        </Stack>
        <DeviceTypeFilter
          value={selectedDeviceType}
          onChange={setSelectedDeviceType}
          options={devicesTypes}
        />
      </Stack>
      <Stack direction="row" flexWrap="wrap" marginBottom={5} gap={2}>
        {visibleDevices.map((device) => {
          const alenShadow: { [key: string]: any } = getAlenDeviceShadow(
            device,
            AlenProductConfig
          );

          const deviceSelected = selectedDeviceIds.includes(device.id);
          return (
            <AlenDeviceCard
              device={device}
              key={device.id}
              checkbox={
                <Checkbox
                  disabled={device.product_name === "AQM"}
                  sx={{
                    alignSelf: "center",
                  }}
                  checked={deviceSelected}
                  onClick={
                    deviceSelected
                      ? deselectDevice(device.id)
                      : selectDevice(device.id)
                  }
                />
              }
              checked={deviceSelected}
              onStatusClick={
                device.online && device.product_name !== "AQM"
                  ? (standardName: string) =>
                      handleStatusClick({
                        device,
                        standardName,
                        currentValue: alenShadow[standardName] as string,
                      })
                  : undefined
              }
            />
          );
        })}
      </Stack>

      {selectedDevice && selectedStandardName && individualActionModalOpened && (
        <AlenIndividualActionModal
          onActionPerformed={() => {
            showSuccessToast();
            refetchDevices();
          }}
          device={selectedDevice}
          selected={currentSelectedValue}
          open={individualActionModalOpened}
          onClose={() => setIndividualActionModalOpened(false)}
          standardName={selectedStandardName}
        />
      )}
      {selectedDevices.length > 0 && (
        <SelectedDeviceActionCard
          setSelectedDeviceIds={setSelectedDeviceIds}
          onActionPerformed={() => {
            showSuccessToast();
            refetchDevices();
          }}
          selectedDevices={selectedDevices}
        />
      )}
      <FirmwareUpdateProgressModal />
      <CheckFirmwareUpdateModal />
      <Toast.Component />
    </FirmwareUpdateContextProvider>
  );
}
